"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import routesfe from "../../Config/RoutesFE";
import SubscriptionService from "./SubscriptionService";
import AddressService from "./AddressService";
import Logger from "../../utils/Logger";
import { pricingPlans } from "../../Config/PricingPlans";
import { clearAllPaymentStorage } from "../../utils/PaymentStorageUtils";

// Components
import PaymentMethodCard from "./PaymentMethodCard";
import OrderSummary from "./OrderSummary";
import AddressCard from "./AddressCard";
import Addressformmodal from "./Addressformmodal";
import configDetails from "../../Config/Config";
import ContactSalesModal from "../../components copy/ContactSalesModal";
import "../../Styles/PaymentPage.css";

function getPayPalReturnParams() {
  const params = new URLSearchParams(window.location.search);
  const token   = params.get("token");     // PayPal orderId
  const payerId = params.get("PayerID");   // present only on approval
  return { token, payerId };
}

const stripePromise = loadStripe(`${configDetails.stripe.publishableKey}` || "pk_test_12345");

// ─── Validators (kept for InlineBillingForm / legacy export) ──────────────────
const VALIDATORS = {
  name: (v) => (!v?.trim() ? "Full name is required." : null),
  email: (v) => {
    if (!v?.trim()) return "Email is required.";
    if (!/^\S+@\S+\.\S+$/.test(v)) return "Please enter a valid email address.";
    return null;
  },
  address: (v) => (!v?.trim() ? "Address is required." : null),
  city: (v) => (!v?.trim() ? "City is required." : null),
  state: (v) => (!v?.trim() ? "State is required." : null),
  country: (v) => {
    if (!v?.trim()) return "Country is required.";
    if (v.trim() !== "US") return "Currently only US is supported.";
    return null;
  },
  postalCode: (v) => {
    if (!v?.trim()) return "ZIP code is required.";
    if (!/^\d{5}(-\d{4})?$/.test(v.trim()))
      return "Enter a valid US ZIP code (e.g. 12345 or 12345-6789).";
    return null;
  },
};

function validateBilling(billing) {
  const errors = {};
  Object.keys(VALIDATORS).forEach((field) => {
    const err = VALIDATORS[field](billing[field], billing.country);
    if (err) errors[field] = err;
  });
  return errors;
}

const TIMEOUT_THRESHOLD_MS = 20_000;

// ─── ProcessingOverlay ────────────────────────────────────────────────────────
function ProcessingOverlay({ visible, isPayPal }) {
  const [longRunning, setLongRunning] = useState(false);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (!visible) { setLongRunning(false); setDots(""); return; }
    const timer = setTimeout(() => setLongRunning(true), TIMEOUT_THRESHOLD_MS);
    const dotInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => { clearTimeout(timer); clearInterval(dotInterval); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Processing your payment"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900/70 via-slate-800/60 to-slate-900/70 backdrop-blur-lg"
      style={{ animation: "fadeIn 0.3s ease-out" }}
    >

      <div
        className="relative bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full mx-4 text-center overflow-hidden"
        style={{ animation: "slideUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
      >
        <div
          className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-white to-blue-50/30 opacity-0"
          style={{ animation: "shimmer 3s ease-in-out infinite" }}
        />

        <div className="relative z-10">
          <div className="flex justify-center mb-10 relative">
            <div
              className="absolute w-16 h-16 rounded-full bg-gradient-to-r from-blue-200 to-blue-300 blur-2xl opacity-60"
              style={{ animation: "pulseGlow 3s ease-in-out infinite" }}
            />
            <div className="relative w-12 h-12 flex items-center justify-center">
              <svg className="w-12 h-12 absolute" viewBox="0 0 100 100" style={{ animation: "spinnerRotate 2s linear infinite" }}>
                <defs>
                  <linearGradient id="spinner-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1d4ed8" />
                    <stop offset="50%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#60a5fa" />
                  </linearGradient>
                  <filter id="blur-filter">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" />
                  </filter>
                </defs>
                <circle cx="50" cy="50" r="45" stroke="#f3e8ff" strokeWidth="3" fill="none" opacity="0.4" />
                <circle cx="50" cy="50" r="45" stroke="url(#spinner-gradient)" strokeWidth="4" fill="none" strokeDasharray="141" strokeDashoffset="20" strokeLinecap="round" filter="url(#blur-filter)" />
              </svg>
            </div>
          </div>

          {longRunning ? (
            <>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Still working{dots}
              </h2>
              <p className="text-gray-600 text-sm mb-3 leading-relaxed">This is taking a bit longer than usual.</p>
              <p className="text-gray-500 text-sm mb-6">Please don't refresh or close this tab.</p>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-lg p-4 mb-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⏳</span>
                  <p className="text-amber-800 text-sm font-medium text-left">
                    If this takes longer than expected,{" "}
                    <a href="mailto:support@noteforge.ai" className="text-amber-700 underline font-semibold hover:text-amber-900 transition-colors">
                      contact support
                    </a>
                  </p>
                </div>
              </div>
            </>
          ) : isPayPal ? (
            <>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-3">
                Redirecting to PayPal{dots}
              </h2>
              <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                We're securely redirecting you to PayPal to complete your payment.
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-400 rounded-lg p-4 mb-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0zm3 0a1 1 0 11-2 0 1 1 0 012 0zm3 0a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                  </svg>
                  <div className="text-left">
                    <p className="text-blue-800 text-sm font-semibold mb-1">Please wait a moment</p>
                    <p className="text-blue-700 text-xs leading-relaxed">
                      Do not refresh, close, or press the back button. You'll be taken to PayPal shortly.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span className="text-green-700 font-medium">Secured with 256-bit SSL encryption</span>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3">
                Processing your payment{dots}
              </h2>
              <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                Please allow a few moments while we securely process your transaction.
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-400 rounded-lg p-4 mb-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="text-xl">🔒</span>
                  <p className="text-purple-800 text-sm font-medium text-left">
                    Do not refresh or navigate away from this page.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span className="text-green-700 font-medium">Secured with 256-bit SSL encryption</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared field helpers (kept for legacy export) ────────────────────────────
function FieldLabel({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
      {children}
      {required && (
        <span className="ml-1 text-red-600 font-bold" aria-hidden="true" title="Required">*</span>
      )}
    </label>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-red-600 flex items-center gap-1 animate-[fadeIn_0.2s_ease-in]">
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  );
}

function fieldInputClass(hasError) {
  const base =
    "w-full px-4 py-2 rounded-lg border text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0";
  if (hasError) {
    return `${base} border-red-500 bg-white focus:ring-red-500 focus:border-red-500 text-gray-900 placeholder-gray-400`;
  }
  return `${base} border-blue-200 bg-white focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400`;
}

// ─── InlineBillingForm (kept but no longer rendered in PaymentFlow JSX) ───────
function InlineBillingForm({ billing, onChange, fieldErrors, onBlur, firstErrorRef }) {
  const handleChange = (field, value) => {
    onChange((prev) => ({ ...prev, [field]: value }));
  };

  const errorFields = Object.keys(fieldErrors);
  const firstErrorField = errorFields.length > 0 ? errorFields[0] : null;

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white rounded-2xl p-8 border border-blue-100">
      <div className="mb-8">
        <div className="space-y-4">
          <div>
            <FieldLabel htmlFor="billing-name" required>Full Name</FieldLabel>
            <input
              id="billing-name"
              ref={firstErrorField === "name" ? firstErrorRef : undefined}
              type="text"
              autoComplete="name"
              placeholder="John Doe"
              value={billing.name}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={() => onBlur("name")}
              className={fieldInputClass(!!fieldErrors.name)}
              aria-invalid={!!fieldErrors.name}
            />
            <FieldError message={fieldErrors.name} />
          </div>
          <div>
            <FieldLabel htmlFor="billing-email" required>Email</FieldLabel>
            <input
              id="billing-email"
              ref={firstErrorField === "email" ? firstErrorRef : undefined}
              type="email"
              autoComplete="email"
              placeholder="john@example.com"
              value={billing.email}
              onChange={(e) => handleChange("email", e.target.value)}
              onBlur={() => onBlur("email")}
              className={fieldInputClass(!!fieldErrors.email)}
              aria-invalid={!!fieldErrors.email}
            />
            <FieldError message={fieldErrors.email} />
          </div>
        </div>
      </div>

      <div className="mb-8 pb-8 border-b border-gray-200">
        <div className="space-y-4">
          <div>
            <FieldLabel htmlFor="billing-address" required>Address</FieldLabel>
            <input
              id="billing-address"
              ref={firstErrorField === "address" ? firstErrorRef : undefined}
              type="text"
              autoComplete="street-address"
              placeholder="123 Main Street"
              value={billing.address}
              onChange={(e) => handleChange("address", e.target.value)}
              onBlur={() => onBlur("address")}
              className={fieldInputClass(!!fieldErrors.address)}
              aria-invalid={!!fieldErrors.address}
            />
            <FieldError message={fieldErrors.address} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <FieldLabel htmlFor="billing-city" required>City</FieldLabel>
              <input
                id="billing-city"
                ref={firstErrorField === "city" ? firstErrorRef : undefined}
                type="text"
                autoComplete="address-level2"
                placeholder="New York"
                value={billing.city}
                onChange={(e) => handleChange("city", e.target.value)}
                onBlur={() => onBlur("city")}
                className={fieldInputClass(!!fieldErrors.city)}
                aria-invalid={!!fieldErrors.city}
              />
              <FieldError message={fieldErrors.city} />
            </div>
            <div>
              <FieldLabel htmlFor="billing-state" required>State</FieldLabel>
              <input
                id="billing-state"
                ref={firstErrorField === "state" ? firstErrorRef : undefined}
                type="text"
                autoComplete="address-level1"
                placeholder="New York"
                value={billing.state}
                onChange={(e) => handleChange("state", e.target.value)}
                onBlur={() => onBlur("state")}
                className={fieldInputClass(!!fieldErrors.state)}
                aria-invalid={!!fieldErrors.state}
              />
              <FieldError message={fieldErrors.state} />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <FieldLabel htmlFor="billing-country" required>Country</FieldLabel>
            <div className="relative">
              <select
                id="billing-country"
                value="US"
                disabled
                className="w-full px-4 py-2 rounded-xl border border-gray-300 bg-blue-50 text-sm text-gray-700 cursor-not-allowed appearance-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="US">United States (US only)</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            </div>
            <p className="mt-2 text-xs text-blue-600 font-medium flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm1 4a1 1 0 00-2 0v4a1 1 0 002 0v-4z" clipRule="evenodd" />
              </svg>
              US-based payments only
            </p>
          </div>
          <div>
            <FieldLabel htmlFor="billing-zip" required>ZIP Code</FieldLabel>
            <input
              id="billing-zip"
              ref={firstErrorField === "postalCode" ? firstErrorRef : undefined}
              type="text"
              autoComplete="postal-code"
              placeholder="10001"
              value={billing.postalCode}
              onChange={(e) => handleChange("postalCode", e.target.value)}
              onBlur={() => onBlur("postalCode")}
              className={fieldInputClass(!!fieldErrors.postalCode)}
              aria-invalid={!!fieldErrors.postalCode}
              maxLength={10}
            />
            <FieldError message={fieldErrors.postalCode} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmitHint({ hasAddress, cardValid, method, paymentTypes, submitAttempted }) {
  const issues = [];
  if (!hasAddress) issues.push("billing address");
  if (method === paymentTypes.STRIPE && !cardValid) issues.push("payment method");

  if (issues.length === 0) return null;

  const noun = issues.length === 1 ? "field" : "fields";

  return (
    <p role="alert" className="mt-3 text-sm text-red-600 text-center font-medium flex items-center justify-center gap-1">
      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm1 4a1 1 0 00-2 0v4a1 1 0 002 0v-4z" clipRule="evenodd" />
      </svg>
      {submitAttempted
        ? `Please complete required ${noun}: ${issues.join(" and ")}.`
        : `Please complete your ${issues.join(" and ")} to continue.`}
    </p>
  );
}

// ─── Address List Modal ──────────────────────────────────────────────────────
function AddressListModal({ 
  addresses, 
  selectedAddressId, 
  onSelect, 
  onClose,
  onAddressDeleted,
  isLoading = false
}) {
  const [deletedAddressIds, setDeletedAddressIds] = React.useState(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);

  const handleDeleteClick = (addressId) => {
    // Check if this is the only address in the list
    if (filteredAddresses.length === 1) {
      Logger.warn("⚠️ Cannot delete the only address", { addressId });
      return;
    }
    setConfirmDeleteId(addressId);
  };

  const handleAddressDeleted = async (deletedId) => {
    // Double-check: prevent deletion of the only address
    if (filteredAddresses.length === 1) {
      Logger.warn("⚠️ Cannot delete the only address", { addressId: deletedId });
      return;
    }
    
    setDeletingId(deletedId);
    try {
      const userId = await AddressService.getUserId();
      await AddressService.deleteAddress(deletedId, userId);
      Logger.info("✅ Address deleted via modal", { addressId: deletedId });
      
      setDeletedAddressIds((prev) => new Set([...prev, deletedId]));
      setConfirmDeleteId(null);
      if (onAddressDeleted) {
        onAddressDeleted(deletedId);
      }
    } catch (err) {
      Logger.error("❌ Failed to delete address in modal", { error: err });
      setConfirmDeleteId(null);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const filteredAddresses = addresses.filter((a) => !deletedAddressIds.has(a.addressId));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        style={{ animation: "slideUpModal 0.25s cubic-bezier(0.34,1.56,0.64,1)", borderRadius: "24px" }}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Saved Addresses</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Address List */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-3">
          {filteredAddresses && filteredAddresses.length > 0 ? (
            filteredAddresses.map((addr) => (
              <div
                key={addr.addressId}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  addr.addressId === selectedAddressId
                    ? "border-blue-500 bg-blue-50 shadow-md shadow-blue-100"
                    : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm cursor-pointer"
                }`}
                onClick={() => {
                  onSelect(addr);
                  onClose();
                }}
              >
                <div className="flex items-start gap-3">
                  {/* Radio indicator */}
                  <div className="flex-shrink-0 mt-0.5">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        addr.addressId === selectedAddressId ? "border-blue-500 bg-blue-500" : "border-gray-300"
                      }`}
                    >
                      {addr.addressId === selectedAddressId && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </div>

                  {/* Address info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 truncate">{addr.fullName}</span>
                      {addr.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Default
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mt-1 leading-snug">
                      {addr.addressLine1}
                    </p>
                    <p className="text-sm text-gray-600 leading-snug">
                      {addr.city}, {addr.state} {addr.postalCode}
                      {addr.country ? `, ${addr.country}` : ""}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{addr.email}</p>
                    {addr.phone && (
                      <p className="text-xs text-gray-400">{addr.phone}</p>
                    )}
                  </div>

                  {/* Delete Button Only */}
                  <div
                    className="flex-shrink-0 flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(addr.addressId)}
                      disabled={deletingId === addr.addressId || filteredAddresses.length === 1}
                      className={`text-xs font-semibold cursor-pointer transition-colors disabled:opacity-40 ${
                        confirmDeleteId === addr.addressId
                          ? 'text-red-700 hover:text-red-800'
                          : 'text-red-600 hover:text-red-700 hover:underline'
                      }`}
                      title={filteredAddresses.length === 1 ? 'Cannot delete the only address' : confirmDeleteId === addr.addressId ? 'Click again to confirm delete' : 'Delete this address'}
                      aria-label={`Delete address for ${addr.fullName}`}
                    >
                      {confirmDeleteId === addr.addressId ? 'Confirm Delete?' : 'Delete'}
                    </button>
                    {confirmDeleteId === addr.addressId && (
                      <button
                        type="button"
                        onClick={() => handleAddressDeleted(addr.addressId)}
                        disabled={deletingId === addr.addressId}
                        className="text-xs font-semibold text-red-600 hover:text-red-700 cursor-pointer transition-colors disabled:opacity-60 flex items-center gap-2"
                      >
                        {deletingId === addr.addressId ? (
                          <>
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-red-200 border-t-red-600 animate-spin inline-block"></div>
                            <span className="ml-2">Deleting...</span>
                          </>
                        ) : (
                          'Yes'
                        )}
                      </button>
                    )}
                    {confirmDeleteId === addr.addressId && (
                      <button
                        type="button"
                        onClick={handleCancelDelete}
                        disabled={deletingId === addr.addressId}
                        className="text-xs font-semibold text-gray-600 hover:text-gray-700 cursor-pointer transition-colors disabled:opacity-40"
                      >
                        No
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-8 w-8 mb-4 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin"></div>
              <p className="text-gray-600 text-sm font-medium">Loading addresses...</p>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-600">No saved addresses yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PaymentFlow ──────────────────────────────────────────────────────────────
function PaymentFlow({ selectedPlan: initialSelectedPlan }) {
  const navigate  = useNavigate();
  const stripe    = useStripe();
  const elements  = useElements();

  // ── SessionStorage keys
  const PLAN_KEY   = "nf_checkout_plan";
  const BILLING_KEY = "nf_checkout_billing";
  const METHOD_KEY  = "nf_checkout_method";
  const CONFIRMED_PRICING_KEY = "nf_checkout_confirmed_pricing";
  const CONFIRMED_ADDRESS_KEY = "nf_checkout_confirmed_address";
  const SHOW_ADDRESS_MODAL_KEY = "nf_checkout_show_address_modal";
  const ADDRESS_MODAL_STATE_KEY = "nf_checkout_address_modal_state";
  const SELECTED_ADDRESS_ID_KEY = "nf_checkout_selected_address_id";

  // ── Legacy billing state (kept for Stripe billing_details and sessionStorage)
  const [billing, setBilling] = useState(() => {
    const stored = sessionStorage.getItem(BILLING_KEY);
    const base = stored
      ? JSON.parse(stored)
      : { name: "", email: "", address: "", city: "", state: "", country: "US", postalCode: "" };
    return { ...base, country: "US" };
  });

  // ── Payment state
  const [method, setMethod]               = useState(() => sessionStorage.getItem(METHOD_KEY) || configDetails.paymentTypes.STRIPE);
  const [cardComplete, setCardComplete]   = useState({ number: false, expiry: false, cvc: false });
  const [submitting, setSubmitting]       = useState(false);
  const [paypalRedirecting, setPaypalRedirecting] = useState(false);
  const [errorMsg, setErrorMsg]           = useState("");

  // ── Address management state ──────────────────────────────────────────────
  const [savedAddresses,   setSavedAddresses]   = useState([]);
  const [selectedAddress,  setSelectedAddress]  = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [addressError,     setAddressError]     = useState(null);
  const [addressModalState, setAddressModalState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(ADDRESS_MODAL_STATE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }); // null | 'add' | address object for edit
  const [showAddressListModal, setShowAddressListModal] = useState(() => 
    sessionStorage.getItem(SHOW_ADDRESS_MODAL_KEY) === "true"
  );
  const [isAddressDefault, setIsAddressDefault] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [pendingDeleteAddressId, setPendingDeleteAddressId] = useState(null);
  const [deletingAddressId, setDeletingAddressId] = useState(null);

  // ── Section confirmation state ──────────────────────────────────────────────
  const [confirmedBillingAddress, setConfirmedBillingAddress] = useState(() => 
    sessionStorage.getItem(CONFIRMED_ADDRESS_KEY) === "true"
  );
  const [confirmedPricingDetails, setConfirmedPricingDetails] = useState(() => 
    sessionStorage.getItem(CONFIRMED_PRICING_KEY) === "true"
  );

  // ── Plan state
  const [selectedPlan, setSelectedPlan]   = useState(() => {
    const stored = sessionStorage.getItem(PLAN_KEY);
    return stored ? JSON.parse(stored) : initialSelectedPlan;
  });
  const [upgradePlan, setUpgradePlan]     = useState(null);
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  // const [touched, setTouched]             = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const firstErrorRef = useRef(null);

  // ── Persist to sessionStorage
  useEffect(() => { sessionStorage.setItem(PLAN_KEY, JSON.stringify(selectedPlan)); }, [selectedPlan]);
  useEffect(() => { sessionStorage.setItem(BILLING_KEY, JSON.stringify(billing)); }, [billing]);
  useEffect(() => { sessionStorage.setItem(METHOD_KEY, method); }, [method]);
  useEffect(() => { sessionStorage.setItem(CONFIRMED_PRICING_KEY, confirmedPricingDetails.toString()); }, [confirmedPricingDetails]);
  useEffect(() => { sessionStorage.setItem(CONFIRMED_ADDRESS_KEY, confirmedBillingAddress.toString()); }, [confirmedBillingAddress]);
  useEffect(() => { sessionStorage.setItem(SHOW_ADDRESS_MODAL_KEY, showAddressListModal.toString()); }, [showAddressListModal]);
  useEffect(() => { sessionStorage.setItem(ADDRESS_MODAL_STATE_KEY, JSON.stringify(addressModalState)); }, [addressModalState]);
  useEffect(() => { 
    if (selectedAddressId) {
      sessionStorage.setItem(SELECTED_ADDRESS_ID_KEY, selectedAddressId);
    }
  }, [selectedAddressId]);

  // ── Update selectedPlan when initialSelectedPlan changes (e.g., user navigates from plans page)
  const prevInitialPlanIdRef = useRef(initialSelectedPlan?.id);
  useEffect(() => {
    if (initialSelectedPlan && initialSelectedPlan.id !== prevInitialPlanIdRef.current) {
      Logger.info("🔄 Plan changed, updating selectedPlan", { 
        from: selectedPlan?.name, 
        to: initialSelectedPlan.name 
      });
      prevInitialPlanIdRef.current = initialSelectedPlan.id;
      setSelectedPlan(initialSelectedPlan);
      // Reset confirmation states when plan changes
      setConfirmedPricingDetails(false);
      setConfirmedBillingAddress(false);
    }
  }, [initialSelectedPlan]);

  const clearCheckoutSession = () => {
    sessionStorage.removeItem(PLAN_KEY);
    sessionStorage.removeItem(BILLING_KEY);
    sessionStorage.removeItem(METHOD_KEY);
    sessionStorage.removeItem(CONFIRMED_PRICING_KEY);
    sessionStorage.removeItem(CONFIRMED_ADDRESS_KEY);
    sessionStorage.removeItem(SHOW_ADDRESS_MODAL_KEY);
    sessionStorage.removeItem(ADDRESS_MODAL_STATE_KEY);
    sessionStorage.removeItem(SELECTED_ADDRESS_ID_KEY);
  };

  // ── Auth check on mount: Clear checkout session if user is not logged in ─────
  useEffect(() => {
    const checkAuthAndResetIfNeeded = async () => {
      try {
        // Try to get user ID - if this fails, user is not authenticated
        await AddressService.getUserId();
        // User is authenticated, proceed normally
      } catch (err) {
        // User is not authenticated - clear all checkout state
        Logger.info("🔄 User not authenticated, resetting checkout session");
        setConfirmedPricingDetails(false);
        setConfirmedBillingAddress(false);
        setShowAddressListModal(false);
        clearCheckoutSession();
      }
    };
    
    checkAuthAndResetIfNeeded();
  }, []);

  // ── Load addresses on mount ───────────────────────────────────────────────
  const loadAddresses = useCallback(async () => {
    setAddressesLoading(true);
    setAddressError(null);
    try {
      const userId    = await AddressService.getUserId();
      let addresses = await AddressService.fetchAddresses(userId);

      // ── Fix data integrity: Ensure only one address is marked as default
      const defaultAddresses = addresses.filter((a) => a.isDefault);
      if (defaultAddresses.length > 1) {
        Logger.warn(`⚠️  Found ${defaultAddresses.length} addresses marked as default. Fixing...`);
        const firstDefault = defaultAddresses[0];
        const restDefault = defaultAddresses.slice(1);
        try {
          for (const addr of restDefault) {
            await AddressService.updateAddress(addr.addressId, userId, {
              ...addr,
              isDefault: false,
            });
            Logger.info(`✅ Unset default flag for address ${addr.addressId}`);
          }
          addresses = addresses.map((a) => ({
            ...a,
            isDefault: a.addressId === firstDefault.addressId,
          }));
          Logger.info("✅ Data integrity restored: only one default address now");
        } catch (repairErr) {
          Logger.error("⚠️  Could not fully repair default address flags", { error: repairErr });
        }
      }

      setSavedAddresses(addresses);

      if (addresses.length > 0) {
        // Always select the latest address (first in array)
        const latestAddress = addresses[0];
        // Persist the latest addressId
        sessionStorage.setItem(SELECTED_ADDRESS_ID_KEY, latestAddress.addressId);

        setSelectedAddress(latestAddress);
        setSelectedAddressId(latestAddress.addressId);
        setIsAddressDefault(latestAddress.isDefault ?? false);
        setBilling((prev) => ({
          ...prev,
          name:       latestAddress.fullName   ?? prev.name,
          email:      latestAddress.email      ?? prev.email,
          address:    latestAddress.addressLine1 ?? prev.address,
          city:       latestAddress.city       ?? prev.city,
          state:      latestAddress.state      ?? prev.state,
          postalCode: latestAddress.postalCode ?? prev.postalCode,
        }));
      }
    } catch (err) {
      Logger.error("❌ Failed to load addresses", { error: err });
      const isAuthError = err?.status === 401 || err?.message?.includes('unauthorized') || err?.message?.includes('auth');
      if (isAuthError) {
        Logger.info("🔄 Authentication error detected, resetting checkout state");
        setConfirmedPricingDetails(false);
        setConfirmedBillingAddress(false);
        clearCheckoutSession();
      }
      setAddressError(err.message || "Failed to load saved addresses.");
    } finally {
      setAddressesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  // Auto-show address list modal when an address is deleted but others remain
  useEffect(() => {
    if (!selectedAddress && savedAddresses.length > 0 && !addressesLoading) {
      setShowAddressListModal(true);
    }
  }, [selectedAddress, savedAddresses.length, addressesLoading]);

  // ── PayPal return handler (runs once on mount, before address fetch matters)
  useEffect(() => {
    const { token, payerId } = getPayPalReturnParams();
    if (!token) return;
    window.history.replaceState({}, document.title, window.location.pathname);

    if (!payerId) {
      Logger.warn("🚫 PayPal payment cancelled by user", { token });
      setErrorMsg("PayPal payment was cancelled. You can try again or choose a different payment method.");
      return;
    }

    Logger.info("💳 PayPal return detected, capturing payment…", { token, payerId });
    setSubmitting(true);

    const pendingRaw   = localStorage.getItem("paypal_pending_order");
    const pendingOrder = pendingRaw ? JSON.parse(pendingRaw) : {};

    SubscriptionService.capturePayPalPayment({ orderId: token, payerId })
      .then((result) => {
        Logger.info("✅ PayPal payment captured", result);
        localStorage.removeItem("paypal_pending_order");
        clearCheckoutSession();
        navigate(routesfe.PAYMENT_SUCCESS, {
          replace: true,
          state: {
            order: {
              orderNumber: result?.orderNumber || result?.order?.orderNumber || token,
              amount: selectedPlan?.price ?? pendingOrder?.price,
              description: `Subscription for ${selectedPlan?.name ?? pendingOrder?.planName}`,
            },
            plan: {
              name: selectedPlan?.name ?? pendingOrder?.planName,
              currencySymbol: selectedPlan?.currencySymbol ?? pendingOrder?.currencySymbol ?? "$",
            },
          },
        });
      })
      .catch((err) => {
        Logger.error("❌ PayPal capture failed", err);
        setErrorMsg(
          err?.message ||
          "We couldn't confirm your PayPal payment. Please contact support if your account was charged."
        );
      })
      .finally(() => setSubmitting(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle logout by clearing checkout session ──────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // When user returns to the tab, check if they're still authenticated
        try {
          await AddressService.getUserId();
          // User is still authenticated, no need to reset
        } catch (err) {
          // User logged out while tab was hidden - reset checkout state
          Logger.info("🔄 Logout detected while tab was hidden, resetting checkout state");
          setConfirmedPricingDetails(false);
          setConfirmedBillingAddress(false);
          clearCheckoutSession();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // ── Address state callbacks ───────────────────────────────────────────────

  const handleAddressSelect = useCallback((address) => {
    Logger.info("📍 Address selected", { addressId: address?.addressId });
    setSelectedAddress(address);
    setSelectedAddressId(address?.addressId);
    setIsAddressDefault(address?.isDefault ?? false);
    // Keep legacy billing state in sync for Stripe billing_details
    if (address) {
      setBilling((prev) => ({
        ...prev,
        name:       address.fullName      ?? prev.name,
        email:      address.email         ?? prev.email,
        address:    address.addressLine1  ?? prev.address,
        city:       address.city          ?? prev.city,
        state:      address.state         ?? prev.state,
        postalCode: address.postalCode    ?? prev.postalCode,
      }));
    }
  }, []);

  const handleAddressAdded = useCallback((newAddress) => {
    // Return the promise so onSave can wait for it
    return loadAddresses();
  }, [loadAddresses]);

  const handleAddressUpdated = useCallback((updatedAddress) => {
    return loadAddresses();
  }, [loadAddresses]);

  const handleDeleteClick = (addressId) => {
    // Check if this is the only address left
    if (savedAddresses.length === 1) {
      setAddressError("You must have at least one address. Please add another address before deleting this one.");
      Logger.warn("⚠️ Cannot delete the only address", { addressId });
      return;
    }
    
    // Clear error if user had seen the warning
    setAddressError(null);
    
    // If this address is already pending confirmation, delete it
    if (pendingDeleteAddressId === addressId) {
      handleConfirmDelete(addressId);
    } else {
      // Otherwise, show confirmation state
      setPendingDeleteAddressId(addressId);
    }
  };

  const handleConfirmDelete = useCallback(async (deletedId) => {
    try {
      // Double-check: prevent deletion of the only address
      if (savedAddresses.length === 1) {
        setAddressError("You must have at least one address. Cannot delete the only address.");
        Logger.warn("⚠️ Cannot delete the only address", { addressId: deletedId });
        return;
      }
      setDeletingAddressId(deletedId);
      Logger.info("🗑️ Deleting address via API", { addressId: deletedId });
      const userId = await AddressService.getUserId();
      await AddressService.deleteAddress(deletedId, userId);
      await loadAddresses();
      setPendingDeleteAddressId(null);
      Logger.info("✅ Address deleted successfully", { addressId: deletedId });
    } catch (err) {
      Logger.error("❌ Failed to delete address", { error: err });
      setAddressError(err.message || "Failed to delete address.");
      setPendingDeleteAddressId(null);
    } finally {
      setDeletingAddressId(null);
    }
  }, [loadAddresses, savedAddresses.length]);

  const handleAddressDeleted = useCallback(async (deletedId) => {
    try {
      // Check if this is the only address left
      if (savedAddresses.length === 1) {
        setAddressError("You must have at least one address. Cannot delete the only address.");
        Logger.warn("⚠️ Cannot delete the only address", { addressId: deletedId });
        return;
      }
      
      await loadAddresses();
      // Close the address list modal
      setShowAddressListModal(false);
      Logger.info("✅ Address deleted and state refreshed", { addressId: deletedId });
    } catch (err) {
      Logger.error("❌ Failed to refresh addresses after deletion", { error: err });
      setAddressError(err.message || "Failed to refresh addresses after deletion.");
    }
  }, [loadAddresses, savedAddresses.length]);

  const handleToggleDefaultAddress = useCallback(async (checked) => {
    if (!selectedAddress) return;
    
    setSettingDefault(true);
    try {
      const userId = await AddressService.getUserId();
      
      if (!checked) {
        // Step 1: Unset the currently selected address as default
        Logger.info(`Unsetting address as default: ${selectedAddress.addressId}`);
        await AddressService.updateAddress(selectedAddress.addressId, userId, {
          ...selectedAddress,
          isDefault: false,
        });
        
        // Step 2: Update local state
        setSavedAddresses((prev) =>
          prev.map((a) => ({
            ...a,
            isDefault: a.addressId === selectedAddress.addressId ? false : a.isDefault,
          }))
        );
        
        // Update selected address
        setSelectedAddress((prev) => ({ ...prev, isDefault: false }));
        setIsAddressDefault(false);
        
        Logger.info("✅ Address unset as default", { addressId: selectedAddress.addressId });
      } else {
        // Step 1: Unset all other addresses as default
        const othersToUnset = savedAddresses.filter(
          (a) => a.addressId !== selectedAddress.addressId && a.isDefault
        );
        
        if (othersToUnset.length > 0) {
          Logger.info(`Unsetting ${othersToUnset.length} other address(es) as default`);
          for (const addr of othersToUnset) {
            try {
              await AddressService.updateAddress(addr.addressId, userId, {
                ...addr,
                isDefault: false,
              });
              Logger.info(`✅ Unset default for address: ${addr.addressId}`);
            } catch (unsetErr) {
              Logger.error(`⚠️  Failed to unset default for ${addr.addressId}`, { error: unsetErr });
              // Continue with other addresses even if one fails
            }
          }
        }
        
        // Step 2: Set the selected address as default
        await AddressService.setDefaultAddress(selectedAddress.addressId, userId, selectedAddress);
        
        // Step 3: Update local state to reflect all changes
        setSavedAddresses((prev) =>
          prev.map((a) => ({
            ...a,
            isDefault: a.addressId === selectedAddress.addressId,
          }))
        );
        
        // Update selected address
        setSelectedAddress((prev) => ({ ...prev, isDefault: true }));
        setIsAddressDefault(true);
        
        Logger.info("✅ Default address updated", { addressId: selectedAddress.addressId });
      }
    } catch (err) {
      Logger.error("❌ Failed to toggle default address", { error: err });
      setAddressError(err.message || "Failed to update default address.");
      // Revert checkbox state on error
      setIsAddressDefault(!checked);
    } finally {
      setSettingDefault(false);
    }
  }, [selectedAddress, savedAddresses]);

  // ── Validation
  // const billingErrors = useMemo(() => validateBilling(billing), [billing]);
  const cardValid     = cardComplete.number && cardComplete.expiry && cardComplete.cvc;

  const canPay =
    !!selectedPlan &&
    !!selectedAddress &&
    !submitting &&
    (method === configDetails.paymentTypes.PAYPAL ||
      (method === configDetails.paymentTypes.STRIPE && cardValid));

  // ── Helpers
  const getNextPlan = (currentPlan, isDowngrade = false) => {
    if (!currentPlan) return null;
    
    // Priority order: Basic Monthly → Professional Monthly → Basic Annual → Professional Annual → Enterprise
    const planPriority = ['BAS', 'PRO', 'BAS-ANN', 'PRO-ANN', 'ENT'];
    const currentCode = (currentPlan.planCode || '').toUpperCase();
    const currentIdx = planPriority.indexOf(currentCode);
    if (currentIdx === -1 || currentIdx === planPriority.length - 1) return null;
    
    const nextCode = planPriority[currentIdx + 1];
    return pricingPlans.find((p) => (p.planCode || '').toUpperCase() === nextCode) || null;
  };

  const isAlreadySubscribedError = (msg) => {
    if (!msg) return false;
    return (
      /already subscribed|already on this plan|subscription exists|active subscription|already has an active subscription|same subscription already active|cannot purchase the same subscription|downgrade/i.test(msg) ||
      (/subscription/i.test(msg) && /plan/i.test(msg))
    );
  };

  // ── Submit / pay ─────────────────────────────────────────────────────────
  const handlePay = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setErrorMsg("");
    setUpgradePlan(null);
    let isPayPalRedirect = false;

    if (!canPay) {
      Logger.warn("🚫 Cannot process payment", {
        hasPlan: !!selectedPlan,
        hasAddress: !!selectedAddress,
        method,
        cardValid,
        submitting,
      });
      setTimeout(() => {
        if (firstErrorRef.current) {
          firstErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          firstErrorRef.current.focus?.();
        }
      }, 100);
      return;
    }

    try {
      setSubmitting(true);
      Logger.info("💳 Starting payment process", {
        plan: selectedPlan.name,
        amount: selectedPlan.price,
        currency: selectedPlan.currency,
        addressId: selectedAddress?.addressId,
      });

      // ── Build billingAddress payload (new format) ─────────────────────
      const billingAddressPayload = AddressService.buildExistingAddressPayload(selectedAddress);

      const order = await SubscriptionService.createPaymentIntent(
        {
          id: selectedPlan.id,
          pricingId: selectedPlan.pricingId ?? null,
          name: selectedPlan.name,
          currency: selectedPlan.currency || "USD",
        },
        billingAddressPayload,
        method
      );

      // ── PAYPAL FLOW ────────────────────────────────────────────────────
      if (method === configDetails.paymentTypes.PAYPAL) {
        const approvalLink  = order?.payment?.approvalLink || order?.approvalLink;
        const paypalOrderId = order?.payment?.orderId     || order?.orderId;

        if (!approvalLink || !paypalOrderId)
          throw new Error("Missing PayPal orderId or approval link");

        const pendingOrderData = {
          planName:       selectedPlan?.name,
          price:          selectedPlan?.price,
          currency:       selectedPlan?.currency || "USD",
          currencySymbol: selectedPlan?.currencySymbol || "$",
        };
        localStorage.setItem("paypal_pending_order", JSON.stringify(pendingOrderData));
        isPayPalRedirect = true;
        setPaypalRedirecting(true);
        window.location.href = approvalLink;
        return;
      }

      // ── STRIPE FLOW ────────────────────────────────────────────────────
      if (!stripe || !elements) {
        Logger.error("🚫 Stripe not initialized");
        return;
      }

      const clientSecret = order?.payment?.stripe?.clientSecret || order?.clientSecret;
      if (!clientSecret) {
        Logger.error("🚫 Missing client secret");
        throw new Error("Missing client secret from server.");
      }

      Logger.info("💳 Confirming card payment");
      const cardElement = elements.getElement("cardNumber");
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            // Use selectedAddress data for Stripe billing_details (kept in sync via billing state)
            name:    billing.name,
            email:   billing.email,
            address: {
              line1:       billing.address,
              city:        billing.city,
              state:       billing.state,
              country:     billing.country,
              postal_code: billing.postalCode,
            },
          },
        },
      });

      if (confirmError) {
        Logger.error("❌ Payment confirmation failed", confirmError);
        throw new Error(confirmError.message);
      }

      if (paymentIntent?.status !== "succeeded") {
        Logger.error("❌ Payment not completed", { status: paymentIntent?.status });
        throw new Error("Payment not completed.");
      }

      Logger.info("✅ Payment successful", {
        paymentIntentId: paymentIntent.id,
        amount: paymentIntent.amount,
        currency: paymentIntent.currency,
      });

      const orderNumber = order?.order?.order?.orderNumber || paymentIntent.id;
      clearCheckoutSession();
      navigate(routesfe.PAYMENT_SUCCESS, {
        replace: true,
        state: {
          paymentIntent,
          order: {
            orderNumber,
            amount: selectedPlan.price,
            description: paymentIntent.description || `Subscription for ${selectedPlan.name}`,
          },
          plan: { name: selectedPlan.name, currencySymbol: selectedPlan.currencySymbol },
        },
      });
    } catch (err) {
      Logger.error("❌ Payment failed", { error: err });
      const msg = err.message || "Payment failed.";
      setErrorMsg(msg);
      if (isAlreadySubscribedError(msg)) {
        const isDowngradeError = /downgrade/i.test(msg);
        // Parse the existing plan code from the error message to suggest upgrade based on existing plan, not requested plan
        const existingMatch = msg.match(/Existing:\s*(\S+)/i);
        const existingCode = existingMatch ? existingMatch[1] : null;
        const existingPlan = existingCode
          ? pricingPlans.find((p) => (p.planCode || '').toUpperCase() === existingCode.toUpperCase())
          : null;
        const nextPlan = getNextPlan(existingPlan || selectedPlan, isDowngradeError);
        setUpgradePlan(nextPlan);
      }
    } finally {
      if (!isPayPalRedirect) setSubmitting(false);
    }
  };

  if (submitting) {
    return <ProcessingOverlay visible={true} isPayPal={paypalRedirecting} />;
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-200 to-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-indigo-200 to-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: "2s" }} />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-300 to-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: "4s" }} />
        </div>

        {/* Header */}
        <div className="relative z-10 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="flex items-center justify-between py-3 sm:py-4">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg sm:text-xl">N</span>
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg truncate">NOTEFORGE</h3>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Secure Checkout</p>
                </div>
              </div>
              <button
                type="button"
                disabled={submitting}
                onClick={() => { clearCheckoutSession(); navigate(routesfe.PLANS); }}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl sm:rounded-2xl hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Plans</span>
              </button>
            </div>

            {/* Progress Bar - Responsive */}
            <div className="pb-4 sm:pb-6 overflow-x-auto">
              <div className="flex items-center justify-between min-w-max sm:min-w-0 px-2 sm:px-0">
                {/* Step 1 */}
                <div className="flex items-center flex-shrink-0">
                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full shadow-lg flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2 sm:ml-3 hidden sm:block">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Plan</p>
                    <p className="text-xs text-gray-600">Done</p>
                  </div>
                </div>

                {/* Line 1 */}
                <div className="flex-1 h-1 mx-1 sm:mx-4 bg-gradient-to-r from-green-500 to-purple-500 rounded-full" />

                {/* Step 2 */}
                <div className="flex items-center flex-shrink-0">
                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full shadow-lg animate-pulse flex-shrink-0">
                    <span className="text-white font-bold text-xs sm:text-sm">2</span>
                  </div>
                  <div className="ml-2 sm:ml-3 hidden sm:block">
                    <p className="text-xs sm:text-sm font-semibold text-purple-600">Payment</p>
                    <p className="text-xs text-gray-600">In Progress</p>
                  </div>
                </div>

                {/* Line 2 */}
                <div className="flex-1 h-1 mx-1 sm:mx-4 bg-gray-200 rounded-full" />

                {/* Step 3 */}
                <div className="flex items-center flex-shrink-0">
                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex-shrink-0">
                    <span className="text-gray-500 font-bold text-xs sm:text-sm">3</span>
                  </div>
                  <div className="ml-2 sm:ml-3 hidden sm:block">
                    <p className="text-xs sm:text-sm font-medium text-gray-500">Confirm</p>
                    <p className="text-xs text-gray-400">Pending</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Banner */}
        <div className="relative z-10 w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center py-2 sm:py-3 px-3 sm:px-4 shadow-md">
          <p className="text-xs sm:text-base font-bold">⚠️ Note: We currently support US-based payments and US($) PayPal accounts.</p>
        </div>
        <div className="relative z-10 text-center py-1.5 sm:py-2 px-3 sm:px-4">
          <p className="text-xs text-gray-600">
            Questions?{" "}
            <a href="mailto:support@noteforge.ai" className="underline font-semibold text-purple-600 hover:text-purple-800">
              Contact us
            </a>
          </p>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Page header */}
          <div className="text-center mb-6 sm:mb-8 lg:mb-10">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent mb-2 sm:mb-3">
              Complete Your Purchase
            </h1>
            <div className="text-center max-w-3xl mx-auto px-2">
              <p className="text-xs sm:text-sm lg:text-lg text-gray-600 leading-relaxed">
                You're about to unlock the full potential of{" "}
                <span className="font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  {selectedPlan.name}
                </span>
                {selectedPlan?.billingCycle && (
                  <span className="billing-badge-header text-blue-600 font-bold ml-2 inline-block transition-all duration-300 hover:text-blue-700 cursor-pointer">
                    <span className="dot-blink"></span> {selectedPlan?.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly'} Plan
                  </span>
                )}
              </p>
              <span className="block text-xs sm:text-xs lg:text-sm text-gray-500 mt-2">Join thousands of users already using NOTEFORGE</span>
            </div>
          </div>

          <form onSubmit={handlePay} noValidate className="w-full flex flex-col lg:flex-row gap-6 sm:gap-8">
            {/* Main Content - Left Column */}
            <div className="flex-1 min-w-0">
              {/* ══════════════════════════════════════════════════════════════ */}
              {/* SECTION 1: PRICE DETAILS (Always Visible) */}
              {/* ══════════════════════════════════════════════════════════════ */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6">
                {/* Step Header - Flipkart Style */}
                <div className="bg-blue-600 text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 rounded-t-xl sm:rounded-t-2xl">
                  <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-white text-blue-600 rounded-full font-bold text-xs sm:text-sm flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold">PLAN DETAILS</h3>
                  </div>
                </div>

                {/* Content - Full Details (Before Confirming) */}
                {!confirmedPricingDetails && (
                  <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    {/* Plan Details */}
                    <div className="border-b border-gray-200 pb-3 sm:pb-4">
                      <h4 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{selectedPlan?.name}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">{selectedPlan?.description || "Professional subscription plan"}</p>
                      <div className="flex items-baseline gap-1 sm:gap-2">
                        <span className="text-xl sm:text-2xl font-bold text-gray-900">
                          {selectedPlan?.currencySymbol}{Number(selectedPlan?.price).toFixed(2)}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">/{selectedPlan?.period?.split(' ')[1] || 'month'}</span>
                      </div>
                    </div>

                    {/* Features/Benefits */}
                    <div className="border-b border-gray-200 pb-3 sm:pb-4">
                      <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-xs sm:text-sm">What's Included:</h4>
                      <ul className="space-y-1.5 sm:space-y-2">
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{selectedPlan?.features?.[0] || "Unlimited conversations"}</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{selectedPlan?.features?.[1] || "Priority support"}</span>
                        </li>
                        <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{selectedPlan?.features?.[2] || "Advanced analytics"}</span>
                        </li>
                      </ul>
                    </div>

                    {/* Total Price */}
                    <div className="flex justify-between items-center bg-blue-50 rounded-lg p-3 sm:p-4">
                      <span className="text-base sm:text-lg font-bold text-gray-900">Total:</span>
                      <span className="text-xl sm:text-2xl font-bold text-blue-600">{selectedPlan?.currencySymbol}{Number(selectedPlan?.price).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Content - Confirmed Summary (After Confirming) */}
                {confirmedPricingDetails && (
                  <div className="p-4 sm:p-6 bg-gray-50 rounded-b-xl sm:rounded-b-2xl">
                    <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm sm:text-base">{selectedPlan?.name}</h4>
                          <p className="text-xs sm:text-sm text-gray-600">{selectedPlan?.currencySymbol}{Number(selectedPlan?.price).toFixed(2)}/{selectedPlan?.period?.split(' ')[1] || 'month'}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmedPricingDetails(false);
                          setConfirmedBillingAddress(false);
                        }}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-blue-600 bg-white border-2 border-blue-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors flex-shrink-0"
                      >
                        VIEW
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Button for Section 1 */}
              {!confirmedPricingDetails && (
                <div className="flex justify-end mb-4 sm:mb-6">
                  <button
                    type="button"
                    onClick={() => setConfirmedPricingDetails(true)}
                    className="px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg sm:rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    Confirm Plan
                  </button>
                </div>
              )}

              {/* ══════════════════════════════════════════════════════════════ */}
              {/* SECTION 2: BILLING ADDRESS */}
              {/* ══════════════════════════════════════════════════════════════ */}
              <fieldset
                disabled={submitting || !confirmedPricingDetails}
                className={`group bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden disabled:opacity-60 mb-4 sm:mb-6 transition-all duration-300 ${
                  !confirmedPricingDetails ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                {/* Step Header - Flipkart Style */}
                <div className={`text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 transition-colors duration-300 rounded-t-xl sm:rounded-t-2xl ${
                  confirmedPricingDetails ? "bg-blue-600" : "bg-gray-400"
                }`}>
                  <div className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full font-bold text-xs sm:text-sm flex-shrink-0 ${
                    confirmedPricingDetails ? "bg-white text-blue-600" : "bg-gray-600 text-gray-300"
                  }`}>
                    2
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold">BILLING ADDRESS</h3>
                  </div>
                </div>

                {/* Content */}
                {confirmedPricingDetails && !confirmedBillingAddress ? (
                  <div className="p-4 sm:p-6">
                    {/* Display Selected Address Card - Before Confirmation */}
                    {selectedAddress && (
                        <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 border-2 border-blue-300 shadow-md hover:shadow-lg transition-all duration-300 mb-4 sm:mb-6">
                          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-6">
                            {/* Left Content - Address Details */}
                            <div className="flex-1 min-w-0 w-full">
                              {/* Name with Default Badge */}
                              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-wrap">
                                <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{selectedAddress?.fullName || "N/A"}</h3>
                                {isAddressDefault && (
                                  <span className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex-shrink-0">
                                    Default
                                  </span>
                                )}
                              </div>

                              {/* Address Line 1 */}
                              <p className="text-xs sm:text-sm text-gray-700 font-medium mb-0.5 sm:mb-1 break-words">
                                {selectedAddress?.addressLine1 || "N/A"}
                              </p>

                              {/* City, State, ZIP, Country */}
                              <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1 break-words">
                                {selectedAddress?.city || "N/A"}, {selectedAddress?.state || "N/A"} {selectedAddress?.postalCode || "N/A"},
                                <span className="ml-1">United States</span>
                              </p>

                              {/* Email */}
                              <p className="text-xs text-gray-500 break-words">{selectedAddress?.email || "N/A"}</p>
                            </div>

                            {/* Right Section - Action Buttons */}
                            <div className="flex gap-2 sm:gap-3 flex-shrink-0 flex-wrap w-full sm:w-auto justify-end">
                              <button
                                type="button"
                                onClick={() => setAddressModalState(selectedAddress)}
                                disabled={deletingAddressId === selectedAddress?.addressId}
                                className="text-xs sm:text-sm font-semibold text-gray-700 hover:text-gray-900 hover:underline cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Edit this address"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(selectedAddress?.addressId)}
                                disabled={savedAddresses.length === 1 || deletingAddressId === selectedAddress?.addressId}
                                className={`text-xs sm:text-sm font-semibold cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                  pendingDeleteAddressId === selectedAddress?.addressId
                                    ? 'text-red-700 hover:text-red-800 underline'
                                    : 'text-red-600 hover:text-red-700 hover:underline'
                                }`}
                                title={savedAddresses.length === 1 ? 'Cannot delete the only address' : pendingDeleteAddressId === selectedAddress?.addressId ? 'Click again to confirm delete' : 'Delete this address'}
                              >
                                {pendingDeleteAddressId === selectedAddress?.addressId ? (
                                  deletingAddressId === selectedAddress?.addressId ? (
                                    <>
                                      <svg className="animate-spin w-2.5 h-2.5 inline-block" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                      </svg>
                                      <span>Deleting...</span>
                                    </>
                                  ) : 'Confirm Delete?'
                                ) : 'Delete'}
                              </button>
                            </div>
                            {/* Cancel Delete Button - Shows when delete is pending */}
                            {pendingDeleteAddressId === selectedAddress?.addressId && (
                              <button
                                type="button"
                                onClick={() => setPendingDeleteAddressId(null)}
                                className="text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-700 transition-colors flex-shrink-0"
                                title="Cancel delete"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Bottom Section - View all saved addresses */}
                      {selectedAddress && (
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => setShowAddressListModal(true)}
                            className="flex items-center gap-1 sm:gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors text-xs sm:text-sm"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            View all Addresses
                          </button>
                        </div>
                      )}

                      {/* Loading state - show when fetching addresses */}
                      {!selectedAddress && addressesLoading && (
                        <div className="flex items-center justify-center py-8 sm:py-12">
                          <div className="text-center max-w-md">
                            <div className="flex justify-center mb-4 sm:mb-6">
                              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin"></div>
                            </div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Loading Addresses</h3>
                            <p className="text-xs sm:text-sm text-gray-600">Please wait while we fetch your saved addresses...</p>
                          </div>
                        </div>
                      )}

                      {/* No address selected message - show only if no saved addresses */}
                      {!selectedAddress && !addressesLoading && savedAddresses.length === 0 && (
                        <div className="text-center py-6 sm:py-8">
                          <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">No saved addresses yet</p>
                          <button
                            type="button"
                            onClick={() => setAddressModalState('add')}
                            className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors gap-1 sm:gap-2"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 4a1 1 0 011 1v6h6a1 1 0 110 2h-6v6a1 1 0 11-2 0v-6H5a1 1 0 110-2h6V5a1 1 0 011-1z" />
                            </svg>
                            Add Address
                          </button>
                        </div>
                      )}

                      {addressModalState !== null && (
                        <Addressformmodal
                          mode={addressModalState === 'add' ? 'add' : 'edit'}
                          address={addressModalState !== 'add' ? addressModalState : null}
                          onSave={async (savedAddress) => {
                            setAddressModalState(null);
                            if (addressModalState === 'add') {
                              // Wait for loadAddresses to complete before setting selected address
                              // This ensures the selected address is displayed instead of the default address
                              await handleAddressAdded(savedAddress);
                              setSelectedAddress(savedAddress);
                              setSelectedAddressId(savedAddress.addressId);
                              setIsAddressDefault(savedAddress.isDefault ?? false);
                            } else {
                              await handleAddressUpdated(savedAddress);
                              setSelectedAddress(savedAddress);
                              setIsAddressDefault(savedAddress.isDefault ?? false);
                            }
                          }}
                          onCancel={() => setAddressModalState(null)}
                        />
                      )}

                      {/* Address List Modal */}
                      {showAddressListModal && (
                        <AddressListModal
                          addresses={savedAddresses}
                          selectedAddressId={selectedAddressId}
                          onSelect={(addr) => {
                            setSelectedAddress(addr);
                            setSelectedAddressId(addr.addressId);
                            setIsAddressDefault(addr.isDefault ?? false);
                          }}
                          onAddressDeleted={handleAddressDeleted}
                          onClose={() => setShowAddressListModal(false)}
                          isLoading={addressesLoading}
                        />
                      )}

                      {/* No address selected warning (after submit attempt) */}
                      {submitAttempted && !selectedAddress && (
                        <div className="mt-3 sm:mt-4 p-3 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl">
                          <p className="text-xs sm:text-sm text-red-700 font-medium flex items-center gap-1 sm:gap-2">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Please select or add a billing address to continue.
                          </p>
                        </div>
                      )}
                  </div>
                ) : confirmedBillingAddress ? (
                  <div className="p-4 sm:p-6">
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-6">
                        {/* Left: Address Summary */}
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 text-xs sm:text-sm uppercase tracking-wide mb-1 sm:mb-2">Billing Address</h4>
                          <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-0.5 sm:mb-1">{selectedAddress?.fullName}</p>
                          <p className="text-xs sm:text-sm text-gray-600 text-left leading-relaxed break-words">
                            {selectedAddress?.addressLine1}, {selectedAddress?.city}, {selectedAddress?.state} {selectedAddress?.postalCode}
                          </p>
                        </div>

                        {/* Right - Change Button */}
                        <button
                          type="button"
                          onClick={() => setConfirmedBillingAddress(false)}
                          className="px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-blue-600 bg-white border-2 border-blue-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors flex-shrink-0"
                        >
                          CHANGE
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}


            </fieldset>

            {/* Separate Add New Address Card - Flipkart Style - Simple */}
            {confirmedPricingDetails && !confirmedBillingAddress && selectedAddress && (
              <div className="mb-4 sm:mb-6">
                <button
                  type="button"
                  onClick={() => setAddressModalState('add')}
                  className="w-full bg-white rounded-lg sm:rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl hover:border-blue-300 transition-all duration-300 flex items-center gap-2 sm:gap-3 text-left"
                >
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 4a1 1 0 011 1v6h6a1 1 0 110 2h-6v6a1 1 0 11-2 0v-6H5a1 1 0 110-2h6V5a1 1 0 011-1z" />
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium text-sm">Add a new address</span>
                </button>
              </div>
            )}

            {/* Confirm Button for Section 2 - Billing Address */}
            {confirmedPricingDetails && !confirmedBillingAddress && selectedAddress && (
              <div className="flex justify-end mb-4 sm:mb-6">
                <button
                  type="button"
                  onClick={() => setConfirmedBillingAddress(true)}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg sm:rounded-xl hover:bg-blue-700 transition-colors"
                >
                  Confirm Address
                </button>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* SECTION 3: PAYMENT METHOD */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <div className={`transition-all duration-300 ${
              confirmedBillingAddress ? "" : "opacity-50 pointer-events-none"
            }`}>
              <fieldset
                disabled={submitting || !confirmedBillingAddress}
                className={`group bg-white rounded-xl sm:rounded-2xl shadow-lg border transition-all duration-300 overflow-hidden disabled:opacity-60 ${
                  confirmedBillingAddress ? "border-gray-200" : "border-gray-200"
                }`}
              >
                {/* Step Header - Flipkart Style */}
                <div className={`text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 transition-colors duration-300 rounded-t-xl sm:rounded-t-2xl ${
                  confirmedBillingAddress ? "bg-blue-600" : "bg-gray-400"
                }`}>
                  <div className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full font-bold text-xs sm:text-sm flex-shrink-0 ${
                    confirmedBillingAddress ? "bg-white text-blue-600" : "bg-gray-600 text-gray-300"
                  }`}>
                    3
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold">PAYMENT METHOD</h3>
                  </div>
                </div>

                {/* Content */}
                {confirmedBillingAddress && (
                <div className="p-4 sm:p-6">
                  <>
                    <PaymentMethodCard
                      method={method}
                      onChangeMethod={setMethod}
                      cardComplete={cardComplete}
                      onCardComplete={setCardComplete}
                      billingPostalCode={billing.postalCode}
                    />

                    {submitAttempted && method === configDetails.paymentTypes.STRIPE && !cardValid && (
                      <FieldError message="Please select a payment method." />
                    )}

                    {/* API Error Banner */}
                    {errorMsg && (
                      <div
                        role="alert"
                        className="mt-4 sm:mt-6 bg-red-50/80 backdrop-blur-xl border border-red-200/50 rounded-lg sm:rounded-2xl p-4 sm:p-6 shadow-lg"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1.293-10.707a1 1 0 011.414 0L10 8.586l1.293-1.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-1 sm:ml-3 flex-1">
                            <h3 className="text-sm sm:text-base text-red-800 font-semibold">Payment Error</h3>
                            <p className="text-xs sm:text-sm text-red-700 mt-1">{errorMsg}</p>
                            {upgradePlan ? (
                              <button
                                type="button"
                                className="mt-3 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-700 hover:via-indigo-700 hover:to-blue-700 text-white font-semibold py-1.5 sm:py-2 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-200 shadow-lg text-xs sm:text-sm"
                                onClick={() => {
                                  if (upgradePlan.name === 'Enterprise') {
                                    setShowContactSalesModal(true);
                                    return;
                                  }
                                  setSelectedPlan(upgradePlan);
                                  setErrorMsg("");
                                  setUpgradePlan(null);
                                  setConfirmedPricingDetails(false);
                                  setConfirmedBillingAddress(false);
                                }}
                              >
                                Upgrade to {upgradePlan.name} ({upgradePlan.currencySymbol}{upgradePlan.price})
                              </button>
                            ) : (
                              isAlreadySubscribedError(errorMsg) && (
                                <div className="mt-3 text-xs sm:text-sm text-gray-700 font-semibold">
                                  You are already on the highest plan. No further upgrades available.
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="pt-4 sm:pt-6 mt-4 sm:mt-6 border-t border-gray-200">
                        <button
                          type="submit"
                          disabled={submitting || isAlreadySubscribedError(errorMsg)}
                          aria-disabled={!canPay || isAlreadySubscribedError(errorMsg)}
                          className={`w-full relative py-4 sm:py-6 px-6 sm:px-8 rounded-lg sm:rounded-2xl font-bold text-base sm:text-xl transition-all duration-300 transform overflow-hidden ${
                            canPay && !submitting && !isAlreadySubscribedError(errorMsg)
                              ? "bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-700 hover:via-indigo-700 hover:to-blue-700 text-white shadow-2xl hover:shadow-3xl hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-purple-500/50 group"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed shadow-lg"
                          }`}
                        >
                          {(canPay && !submitting && !isAlreadySubscribedError(errorMsg)) && (
                            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                          )}
                          {submitting ? (
                            <div className="flex items-center justify-center relative z-10 gap-2 sm:gap-3">
                              <div className="h-4 w-4 sm:h-6 sm:w-6 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                              <span className="text-xs sm:text-base">Processing your payment…</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center relative z-10 gap-2">
                              <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              <span className="text-xs sm:text-base">Complete Payment • {selectedPlan?.currencySymbol}{Number(selectedPlan?.price).toFixed(2)}</span>
                            </div>
                          )}
                        </button>

                        {/* "Why disabled?" hint */}
                        {!canPay && !submitting && (
                          <SubmitHint
                            hasAddress={!!selectedAddress}
                            cardValid={cardValid}
                            method={method}
                            paymentTypes={configDetails.paymentTypes}
                            submitAttempted={submitAttempted}
                          />
                        )}

                        {/* Security Notice */}
                        <div className="mt-4 sm:mt-6 bg-emerald-50/80 backdrop-blur-xl rounded-lg sm:rounded-2xl p-3 sm:p-4 border border-emerald-200/50">
                          <div className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-emerald-800">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <p className="font-semibold mb-0.5">🔒 Bank-level Security</p>
                              <p className="text-emerald-700 text-xs">
                                Your payment is protected with 256-bit SSL encryption and processed securely through{" "}
                                {method === configDetails.paymentTypes.PAYPAL ? "PayPal" : "Stripe"}.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Trust Indicators */}
                        <div className="mt-4 sm:mt-6 flex items-center justify-center gap-4 sm:gap-8 flex-wrap opacity-60">
                          {["SSL Secured", "PCI Compliant", "Money-back Guarantee"].map((label) => (
                            <div key={label} className="flex items-center text-xs text-gray-600">
                              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {label}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                </div>
                )}
              </fieldset>
            </div>
            {/* End: Main Content - Left Column */}
            </div>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* SIDEBAR: ORDER SUMMARY (Always Visible on Large Screens) */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <aside className="w-full lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden lg:sticky lg:top-8">
                {/* Header */}
                <div className="bg-blue-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl">
                  <h3 className="text-base sm:text-lg font-bold">ORDER SUMMARY</h3>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                  <OrderSummary plan={selectedPlan} onBack={() => navigate(-1)} />
                </div>
              </div>
            </aside>
          </form>
        </div>
      </div>
      <ContactSalesModal
        isOpen={showContactSalesModal}
        onClose={() => setShowContactSalesModal(false)}
        planName="Enterprise"
      />
    </>
  );
}

// ─── PaymentPage (root export) ────────────────────────────────────────────────
export default function PaymentPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedPlan = state?.selectedPlan;

  useEffect(() => {
    if (!selectedPlan) {
      Logger.warn("🚫 No plan selected, redirecting to home");
      navigate(routesfe.HOME, { replace: true });
    } else {
      Logger.info("📄 Payment page loaded", {
        plan: selectedPlan.name,
        price: selectedPlan.price,
        currency: selectedPlan.currency,
      });
    }
  }, [selectedPlan, navigate]);

  if (!selectedPlan) return null;

  return (
    <div className="no-sidebar-page">
      <Elements stripe={stripePromise}>
        <PaymentFlow selectedPlan={selectedPlan} />
      </Elements>
    </div>
  );
}

// Legacy exports kept for any other files that import from PaymentPage
export { FieldLabel, FieldError, ProcessingOverlay, InlineBillingForm, validateBilling, VALIDATORS, fieldInputClass };